// =====================================================================
// fileService (PME Sprint 3a / S52) — arquivos do Prontuário (EHR).
// Começa pela FOTO do paciente. Exames/anexos entram no próximo slice.
//
// 🔒 LGPD: o serving é auth-gated. A imagem é buscada como BLOB (com o token
// do axios) e exibida via objectURL — um <img src="/patients/x/photo"> direto
// NÃO levaria o header Authorization, então não funcionaria (e nem deve: dado
// médico nunca é URL pública).
// =====================================================================
import api from "./api";

// Upload/troca a foto. Mesmo padrão dos outros uploads do CRM:
// api.post(url, FormData) SEM setar Content-Type (o axios monta o boundary).
export const uploadPatientPhoto = async (patientId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post(`/patients/${patientId}/photo`, formData);
  return data; // paciente atualizado (com photoUrl)
};

// Busca a foto como blob (com token) e devolve um objectURL pronto pro Avatar.
// Lança em 404 (sem foto) — quem chama trata como "sem foto".
export const getPatientPhotoObjectUrl = async (patientId) => {
  const { data } = await api.get(`/patients/${patientId}/photo`, {
    responseType: "blob"
  });
  return URL.createObjectURL(data);
};

export const deletePatientPhoto = async (patientId) => {
  await api.delete(`/patients/${patientId}/photo`);
};

// Validação client-side (espelha o backend: PNG/JPG/WEBP, 5 MB).
export const PHOTO_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export const validatePhotoFile = (file) => {
  if (!file) return "Selecione uma imagem.";
  const okTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  if (!okTypes.includes(file.type)) return "Formato inválido. Use PNG, JPG ou WEBP.";
  if (file.size > PHOTO_MAX_BYTES) return "Imagem muito grande (máx. 5 MB).";
  return null;
};

// Traduz o erro do backend numa mensagem amigável.
export const photoErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const map = {
    ERR_PHOTO_TOO_LARGE: "Imagem muito grande (máx. 5 MB).",
    ERR_UPLOAD_MIMETYPE: "Formato inválido. Use PNG, JPG ou WEBP.",
    ERR_PHOTO_FILE_REQUIRED: "Selecione uma imagem.",
    ERR_UPLOAD_CONTEXT: "Não foi possível identificar o paciente.",
    ERR_PATIENT_NOT_FOUND: "Paciente não encontrado."
  };
  if (code && map[code]) return map[code];
  return "Não foi possível enviar a foto. Tente novamente.";
};

// ============================ EXAMES / anexos (3a-ii) ============================

// Anexa um exame/documento ao paciente (PDF ou imagem) + descrição opcional.
export const uploadPatientDocument = async (patientId, file, description) => {
  const formData = new FormData();
  formData.append("file", file);
  if (description) formData.append("description", description);
  const { data } = await api.post(`/patients/${patientId}/documents`, formData);
  return data; // attachment criado
};

// Lista os anexos do paciente.
export const listPatientDocuments = async (patientId) => {
  const { data } = await api.get(`/patients/${patientId}/documents`);
  return data; // { documents: [...] }
};

// Busca um anexo como blob (com token) e devolve um objectURL (ver/baixar).
export const getEhrFileObjectUrl = async (id) => {
  const { data } = await api.get(`/ehr/files/${id}`, { responseType: "blob" });
  return URL.createObjectURL(data);
};

export const deleteEhrFile = async (id) => {
  await api.delete(`/ehr/files/${id}`);
};

// Validação client-side (espelha o backend: PDF/PNG/JPG/WEBP, 10 MB).
export const DOC_ACCEPT = "application/pdf,image/png,image/jpeg,image/jpg,image/webp";
export const DOC_MAX_BYTES = 10 * 1024 * 1024;

export const validateDocFile = (file) => {
  if (!file) return "Selecione um arquivo.";
  const okTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
  if (!okTypes.includes(file.type)) return "Formato inválido. Use PDF, PNG, JPG ou WEBP.";
  if (file.size > DOC_MAX_BYTES) return "Arquivo muito grande (máx. 10 MB).";
  return null;
};

export const docErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const map = {
    ERR_FILE_TOO_LARGE: "Arquivo muito grande (máx. 10 MB).",
    ERR_UPLOAD_MIMETYPE: "Formato inválido. Use PDF, PNG, JPG ou WEBP.",
    ERR_FILE_REQUIRED: "Selecione um arquivo.",
    ERR_UPLOAD_CONTEXT: "Não foi possível identificar o paciente.",
    ERR_PATIENT_NOT_FOUND: "Paciente não encontrado.",
    ERR_FILE_NOT_FOUND: "Arquivo não encontrado."
  };
  if (code && map[code]) return map[code];
  return "Não foi possível concluir a operação. Tente novamente.";
};

// ===================== DOCUMENTOS EMITIDOS (3b — receita/atestado/pedido) =====================

export const DOC_TYPE_LABELS = {
  prescription: "Receita",
  medical_certificate: "Atestado",
  exam_request: "Pedido de exame",
  referral: "Encaminhamento",
  tcle: "TCLE"
};

// Interpretação de um exame por IA (Claude lê o PDF/imagem). Orientativo.
export const interpretExam = async (attachmentId) => {
  const { data } = await api.post(`/ehr/files/${attachmentId}/interpret`);
  return data; // { tipoExame, resumo, alteracoes, condutaSugerida, gravidade, observacao, disclaimer }
};

// Emite um documento (gera PDF no servidor). payload:
//   { documentType, title?, body, professionalName?, professionalId?, encounterId? }
export const emitDocument = async (patientId, payload) => {
  const { data } = await api.post(`/patients/${patientId}/emit-document`, payload);
  return data; // documento criado (com pdfUrl)
};

export const listEmittedDocuments = async (patientId) => {
  const { data } = await api.get(`/patients/${patientId}/emitted-documents`);
  return data; // { documents: [...] }
};

// Busca o PDF do documento como blob (com token) → objectURL.
export const getEmittedDocObjectUrl = async (id) => {
  const { data } = await api.get(`/ehr/documents/${id}/pdf`, { responseType: "blob" });
  return URL.createObjectURL(data);
};

export const deleteEmittedDoc = async (id) => {
  await api.delete(`/ehr/documents/${id}`);
};

export const emitDocErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const map = {
    ERR_DOC_TYPE_INVALID: "Tipo de documento inválido.",
    ERR_DOC_BODY_REQUIRED: "Escreva o conteúdo do documento.",
    ERR_DOC_RENDER: "Falha ao gerar o PDF. Tente novamente.",
    ERR_DOC_NOT_FOUND: "Documento não encontrado.",
    ERR_PATIENT_NOT_FOUND: "Paciente não encontrado."
  };
  if (code && map[code]) return map[code];
  return "Não foi possível emitir o documento. Tente novamente.";
};

// Envia um documento emitido pro paciente.
// payload: { channel:'whatsapp'|'email', to, whatsappId?, caption? }
export const sendEmittedDocument = async (documentId, payload) => {
  const { data } = await api.post(`/ehr/documents/${documentId}/send`, payload);
  return data; // { sent:true, channel, to, ticketId? }
};

export const sendDocErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const map = {
    ERR_DOC_NOT_FOUND: "Documento não encontrado.",
    ERR_PATIENT_NOT_FOUND: "Paciente não encontrado.",
    ERR_SEND_CHANNEL_INVALID: "Canal de envio inválido.",
    ERR_NO_DEST_PHONE: "Informe um número de WhatsApp válido (com DDD).",
    ERR_NUMBER_NOT_ON_WHATSAPP: "Esse número não tem WhatsApp ativo. Confira o DDD e o 9 após o DDD.",
    ERR_NO_DEST_EMAIL: "Informe um e-mail válido.",
    ERR_NO_WAPP: "Conexão de WhatsApp não encontrada.",
    ERR_NO_DEF_WAPP_FOUND: "Nenhum WhatsApp conectado na clínica no momento.",
    ERR_WAPP_NOT_CONNECTED: "O WhatsApp da clínica está desconectado no momento.",
    ERR_NO_CONTACT: "Não foi possível resolver o contato do paciente.",
    ERR_MAIL_NOT_CONFIGURED: "Envio por e-mail ainda não está configurado (SMTP da clínica).",
    ERR_MAIL_SEND_FAILED: "Falha ao enviar o e-mail. Confira o endereço e tente de novo.",
    ERR_SENDING_WAPP_MSG: "Falha ao enviar pelo WhatsApp. Tente novamente."
  };
  // tolerante a sufixos (ex.: "ERR_NO_DEF_WAPP_FOUND in COMPANY 1")
  const key = code && Object.keys(map).find((k) => String(code).startsWith(k));
  if (key) return map[key];
  return "Não foi possível enviar o documento. Tente novamente.";
};
